import { useEffect, useState } from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { useTheme, type Theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface ThemeToggleProps {
  className?: string;
  showLabels?: boolean;
  size?: "sm" | "md" | "lg";
}

export function ThemeToggle({ className, showLabels = false, size = "sm" }: ThemeToggleProps) {
  const { theme, effective, changeTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const options: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Theme selection"
      className={cn(
        "theme-selector-pill inline-flex items-center rounded-full border border-border/80 bg-surface/80 p-0.5 backdrop-blur-md transition-all shadow-sm",
        showLabels ? "gap-1 p-1" : "gap-0.5",
        className,
      )}
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        // Determine if selected (fall back gracefully before mount)
        const isSelected = mounted ? theme === opt.value : opt.value === "system";
        const isEffective = mounted ? effective === opt.value : false;

        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => changeTheme(opt.value)}
            title={`${opt.label} mode${opt.value === "system" ? " (matches device)" : ""}${isSelected ? " (active)" : ""}`}
            aria-label={`Select ${opt.label} theme`}
            className={cn(
              "group relative inline-flex items-center justify-center rounded-full text-xs font-medium transition-all duration-200 cursor-pointer select-none",
              showLabels ? "px-3 py-1.5 gap-1.5" : "h-7 w-7 p-0",
              size === "lg" && (showLabels ? "px-4 py-2 text-sm" : "h-8 w-8"),
              isSelected
                ? "bg-card text-foreground shadow-sm ring-1 ring-border/50 font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
            )}
          >
            <Icon
              className={cn(
                "shrink-0 transition-transform duration-200",
                showLabels ? "size-3.5" : "size-3.5",
                isSelected && "text-primary scale-110",
                !isSelected && "group-hover:scale-105",
              )}
            />
            {showLabels && <span className="leading-none tracking-tight">{opt.label}</span>}
            {isSelected && opt.value === "system" && (
              <span
                className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-primary"
                title={`System mode active (currently ${effective})`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
